import { defineComponent } from 'vue'
import type {
  NavigationGuardNext,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded
} from 'vue-router'
import { createRouter, createWebHistory } from 'vue-router'

import { catalogRoutes } from '@/modules/catalog/routes.ts'
import { dailyViewRoutes } from '@/modules/daily-view/routes'
import { promotionsRoutes } from '@/modules/promotions/routes'
import { reportsRoutes } from '@/modules/reports/routes'
import pinia from '@/store'
import { useAuthStore } from '@/store/auth.js'

const PageNotFound = defineComponent({
  created: function () {
    window.location.href = '/'
  }
})

const routes = [
  {
    path: '/',
    component: () => import('./views/Dashboard.vue'),
    redirect: {
      name: 'home'
    },
    children: [
      {
        name: 'home',
        path: '/home',
        component: () => import('./components/Home.vue')
      },
      ...dailyViewRoutes,
      {
        name: 'config',
        path: '/config',
        component: () => import('./components/Config.vue')
      },
      {
        name: 'subscription',
        path: '/subscription',
        component: () => import('@/modules/subscription/views/Subscription.vue')
      },
      {
        name: 'printers',
        path: '/printers',
        component: () => import('./components/Printers.vue')
      },
      ...catalogRoutes,
      {
        name: 'delivery',
        path: '/delivery',
        component: () => import('@/components/Delivery.vue')
      },
      {
        name: 'ordersLimit',
        path: '/orders-limit',
        component: () => import('@/components/OrderingLimits.vue')
      },
      {
        name: 'floorPlan',
        path: '/floor-plan',
        component: () => import('@/modules/floorplan/views/Floorplan.vue')
      },
      {
        name: 'devices',
        path: '/devices',
        component: () => import('@/components/Devices.vue')
      },
      {
        name: 'integrations',
        path: '/integrations',
        component: () => import('@/components/integrations/Integrations.vue')
      },
      {
        name: 'integrationValidate',
        path: '/integrations/:id/validate',
        component: () =>
          import('@/components/integrations/IntegrationValidator.vue')
      },
      {
        name: 'employees',
        path: '/employees',
        component: () => import('@/components/Employees.vue')
      },
      {
        name: 'posConfig',
        path: '/pos-config',
        component: () => import('@/components/PosConfig.vue')
      },
      {
        name: 'fastModeConfig',
        path: '/fast-mode-config',
        component: () => import('@/components/FastModeConfig.vue')
      },
      {
        path: '/legal-entities',
        name: 'legalEntities',
        redirect: '/legal-entities/',
        children: [
          {
            path: '',
            name: 'legalEntitiesList',
            component: () => import('@/components/LegalEntities.vue')
          },
          {
            name: 'legalEntity',
            path: ':legalEntityId',
            component: () => import('@/components/LegalEntity.vue')
          }
        ]
      },
      {
        name: 'cardRequest',
        path: '/legal-entities/:legalEntityId/card-request',
        component: () => import('@/components/CardRequest.vue')
      },
      {
        name: 'invoice',
        path: '/invoices/:id',
        component: () => import('@/modules/billing/views/Invoice.vue')
      },
      {
        name: 'virtualBrands',
        path: '/virtual-brands',
        component: () => import('@/components/VirtualBrands.vue')
      },
      {
        name: 'virtualBrand',
        path: '/virtual-brands/:virtualBrandId',
        component: () => import('@/components/VirtualBrand.vue')
      },
      {
        name: 'locations',
        path: '/locations',
        component: () => import('@/modules/locations/views/Locations.vue')
      },
      {
        name: 'customers',
        path: '/customers',
        component: () => import('@/views/Customers.vue')
      },
      {
        name: 'customer',
        path: '/customers/:customerId',
        component: () => import('@/views/Customer.vue')
      },
      {
        name: 'users',
        path: '/users',
        component: () => import('@/components/Users.vue')
      },
      ...promotionsRoutes,
      {
        name: 'movementsList',
        path: '/movements',
        component: () => import('@/modules/billing/views/Movements.vue')
      },
      {
        name: 'payoutsList',
        path: '/payouts',
        component: () => import('@/modules/billing/views/Payouts.vue')
      },
      {
        name: 'payout',
        path: '/payouts/:id',
        component: () => import('@/modules/billing/views/Payout.vue')
      },
      {
        name: 'locationShop',
        path: '/location/shop',
        component: () => import('@/shop/views/LocationShop.vue')
      },
      {
        name: 'QR',
        path: '/location/QR',
        component: () => import('@/components/QR.vue')
      },
      {
        name: 'organizationShop',
        path: '/organization/shop',
        component: () => import('@/shop/views/OrganizationShop.vue')
      },
      {
        name: 'organizationBookings',
        path: '/organization/bookings',
        component: () =>
          import('@/modules/bookings/view/OrganizationConfig.vue')
      },
      {
        name: 'schedules',
        path: '/schedules',
        component: () => import('@/components/Schedules.vue')
      },
      {
        name: 'temporalClosures',
        path: '/temporal-closures',
        component: () => import('@/components/TemporalClosures.vue')
      },
      {
        path: '/location/bookings',
        children: [
          {
            name: 'bookingsConfig',
            path: 'config',
            component: () => import('@/components/BookingsConfig.vue')
          },
          {
            name: 'bookingsManager',
            path: 'manager',
            component: () => import('@/modules/bookings/view/Manager.vue')
          },
          {
            name: 'bookingsCommunications',
            path: 'communications',
            component: () =>
              import('@/modules/bookings/view/Communications.vue')
          },
          {
            name: 'bookingsAvailability',
            path: 'availability',
            component: () => import('@/modules/bookings/view/Availability.vue')
          },
          {
            name: 'bookingsPolicy',
            path: 'policies',
            component: () => import('@/modules/bookings/view/Policies.vue')
          },
          {
            name: 'bookingsShifts',
            path: 'shifts',
            component: () => import('@/modules/bookings/view/Shifts.vue')
          }
        ]
      },
      {
        name: 'lastProducts',
        path: '/last-products',
        component: () => import('@/components/LastProducts.vue')
      },
      ...reportsRoutes
    ]
  },
  {
    name: 'createOrganization',
    path: '/createOrganization',
    component: () =>
      import('@/modules/new-organization/views/NewOrganization.vue')
  },
  {
    name: 'login',
    path: '/login',
    component: () => import('@/components/Login.vue')
  },
  {
    name: 'supportLogin',
    path: '/support/login',
    component: () => import('@/components/SupportLogin.vue')
  },
  {
    name: 'redirectLogin',
    path: '/redirect/login',
    component: () => import('@/components/RedirectLogin.vue')
  },
  {
    name: 'signup',
    path: '/signup',
    component: () => import('@/components/SignUpWrapper.vue')
  },
  {
    name: 'createUser',
    path: '/create-user',
    component: () => import('@/components/SignUp.vue')
  },
  {
    name: 'onboarding',
    path: '/onboarding',
    component: () => import('./components/onboarding/Onboarding.vue')
  },
  {
    name: 'installation',
    path: '/installation',
    component: () => import('./components/onboarding/Installation.vue')
  },
  {
    name: 'newPassword',
    path: '/new-password/:recoverId?/:token?',
    component: () => import('./components/NewPassword.vue')
  },
  {
    name: 'rating',
    path: '/rating/:tabId/vote/:rate',
    component: () => import('@/components/Rating.vue')
  },
  {
    name: 'externalOAuth',
    path: '/external-oauth/:integration',
    component: () => import('@/components/ExternalOAuth.vue')
  },
  { path: '/:notvalid(.*)', component: PageNotFound }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

router.beforeEach(
  (
    to: RouteLocationNormalized,
    _from: RouteLocationNormalizedLoaded,
    next: NavigationGuardNext
  ): void => {
    const auth = useAuthStore(pinia)
    const needsAuth = ![
      'login',
      'signup',
      'supportLogin',
      'redirectLogin',
      'newPassword',
      'rating',
      'createUser'
    ].includes(to.name as string)
    const isAuthenticated = auth.isAuthenticated
    if (needsAuth && !isAuthenticated) {
      next({
        name: 'login',
        query: { redirect: to.name ? to.name.toString() : '' }
      })
    } else {
      next()
    }
  }
)

export default router
